import { FloatingPortal } from "@floating-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { debounce } from "lodash";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

// Size-based configurations for tooltip
const SIZE_CONFIGS = {
  xs: {
    width: "180px",
    padding: 0.5,
    rowHeight: "20px",
    iconSize: "10px",
  },
  sm: {
    width: "210px",
    padding: 0.75,
    rowHeight: "22px",
    iconSize: "11px",
  },
  md: {
    width: "240px",
    padding: 1,
    rowHeight: "24px",
    iconSize: "12px",
  },
  lg: {
    width: "270px",
    padding: 1.25,
    rowHeight: "26px",
    iconSize: "14px",
  },
};

interface ChartTooltipRowProps {
  value: unknown;
  index: number;
  datum: unknown;
  isActive: boolean;
  size?: "xs" | "sm" | "md" | "lg";
  colors?: string[];
  renderKeyColumn: (
    datum: unknown,
    value: unknown,
    index: number,
  ) => React.ReactNode;
  renderValueColumn: (
    datum: unknown,
    value: unknown,
    index: number,
  ) => React.ReactNode;
}

const ChartTooltipRow = memo(
  ({
    value,
    index,
    datum,
    isActive,
    size = "md",
    colors = [],
    renderKeyColumn,
    renderValueColumn,
  }: ChartTooltipRowProps) => {
    const sizeConfig = SIZE_CONFIGS[size];

    return (
      <div
        className={cn(
          "flex flex-row justify-between w-[calc(100%)] h-(--row-height) max-w-[calc(100%)] items-center",
          index > 0 &&
            "[border-top-width:1px] [border-top-style:solid] border-border",
        )}
        style={
          {
            "--row-height":
              Number(sizeConfig.rowHeight) <= 1
                ? `${Number(sizeConfig.rowHeight) * 100}%`
                : sizeConfig.rowHeight,
          } as CSSProperties
        }
      >
        <div className="[border-right-width:1px] [border-right-style:solid] h-[calc(100%)] min-h-[calc(100%)] flex items-center border-border justify-center pl-2 pr-2 pt-0 pb-0 mr-2">
          <div
            // ``background``, not ``backgroundColor``: a part with nothing
            // in it is handed the hatch it was drawn with, not a colour.
            className="w-(--icon-size) h-(--icon-size) [background:var(--background)] rounded-[4px]"
            style={
              {
                "--icon-size":
                  Number(sizeConfig.iconSize) <= 1
                    ? `${Number(sizeConfig.iconSize) * 100}%`
                    : sizeConfig.iconSize,
                "--background": colors[index % colors.length],
              } as CSSProperties
            }
          />
        </div>

        <Typography
          // The name gives way only once the tooltip has grown as far as it
          // is allowed to; the figure beside it never does.
          className={cn(
            "truncate",
            isActive ? "text-foreground" : "text-muted-foreground/80",
            isActive ? "opacity-[1]" : "opacity-[0.5]",
            "min-w-0",
          )}
        >
          {renderKeyColumn(datum, value, index)}
        </Typography>
        <div className="grow min-w-4" />

        <Typography
          className={cn(
            "truncate",
            isActive ? "text-foreground" : "text-muted-foreground/80",
            isActive ? "opacity-[1]" : "opacity-[0.5]",
            "min-w-max",
          )}
        >
          {renderValueColumn(datum, value, index)}
        </Typography>
      </div>
    );
  },
);

ChartTooltipRow.displayName = "ChartTooltipRow";

interface ChartTooltipContentProps {
  values: unknown[];
  datum: unknown;
  keyColumnHeader: string | null;
  valueColumnHeader: string | null;
  size?: "xs" | "sm" | "md" | "lg";
  colors?: string[];
  renderKeyColumn: (
    datum: unknown,
    value: unknown,
    index: number,
  ) => React.ReactNode;
  renderValueColumn: (
    datum: unknown,
    value: unknown,
    index: number,
  ) => React.ReactNode;
  checkIsActiveValue: (
    datum: unknown,
    value: unknown,
    index: number,
  ) => boolean;
}

const ChartTooltipContent = memo(
  ({
    values,
    datum,
    keyColumnHeader,
    valueColumnHeader,
    size = "md",
    colors = [],
    renderKeyColumn,
    renderValueColumn,
    checkIsActiveValue,
  }: ChartTooltipContentProps) => {
    const sizeConfig = SIZE_CONFIGS[size];
    return (
      <div
        // As wide as what it has to say, down to a width that still reads
        // as a table and up to one that still fits beside a chart. Fixed,
        // it cut every name off at the same unhelpful place: "Analytics &
        // …" tells you nothing that "Analytics" did not.
        className="bg-popover p-(--padding) flex flex-col w-max min-w-(--width) max-w-[min(420px,90vw)] rounded-sm border-border pointer-events-none"
        style={
          {
            "--padding": `${sizeConfig.padding * 8}px`,
            "--width":
              Number(sizeConfig.width) <= 1
                ? `${Number(sizeConfig.width) * 100}%`
                : sizeConfig.width,
          } as CSSProperties
        }
      >
        <div
          className="flex flex-row justify-between sticky h-(--row-height) top-0 w-[calc(100%)] [border-bottom-width:1px] [border-bottom-style:solid] border-border"
          style={
            {
              "--row-height":
                Number(sizeConfig.rowHeight) <= 1
                  ? `${Number(sizeConfig.rowHeight) * 100}%`
                  : sizeConfig.rowHeight,
            } as CSSProperties
          }
        >
          <div className="pl-3.5 pr-3.5 [border-right-width:1px] [border-right-style:solid] border-border" />
          <Typography className="truncate text-muted-foreground/80 pl-2">
            {keyColumnHeader}
          </Typography>
          <div className="grow ml-2" />
          <Typography className="text-muted-foreground/80">
            {valueColumnHeader}
          </Typography>
        </div>
        {values.map((value, index) => (
          <ChartTooltipRow
            key={index}
            value={value}
            index={index}
            datum={datum}
            size={size}
            colors={colors}
            isActive={checkIsActiveValue(datum, value, index)}
            renderKeyColumn={renderKeyColumn}
            renderValueColumn={renderValueColumn}
          />
        ))}
      </div>
    );
  },
);

ChartTooltipContent.displayName = "ChartTooltipContent";

interface Position {
  x: number;
  y: number;
}

interface ChartTooltipProps {
  values: unknown[];
  datum: unknown;
  keyColumnHeader?: string | null;
  valueColumnHeader?: string | null;
  size?: "xs" | "sm" | "md" | "lg";
  colors?: string[];
  renderKeyColumn?: (
    datum: unknown,
    value: unknown,
    index: number,
  ) => React.ReactNode;
  renderValueColumn?: (
    datum: unknown,
    value: unknown,
    index: number,
  ) => React.ReactNode;
  checkIsActiveValue?: (
    datum: unknown,
    value: unknown,
    index: number,
  ) => boolean;
}

export function ChartTooltip({
  values,
  datum,
  keyColumnHeader = null,
  valueColumnHeader = null,
  size = "md",
  colors = [],
  renderKeyColumn = () => null,
  renderValueColumn = () => null,
  checkIsActiveValue = () => false,
}: ChartTooltipProps) {
  const positionRef = useRef<Position>({ x: 0, y: 0 });
  const cardRef = useRef<HTMLDivElement>(null);
  const isVisibleRef = useRef(false);
  const [, setForceUpdate] = useState(false);
  const forceUpdate = useCallback(() => setForceUpdate((prev) => !prev), []);

  const hideTooltip = useCallback(
    debounce(() => {
      isVisibleRef.current = false;
      forceUpdate();
    }, 500),
    [forceUpdate],
  );

  useEffect(() => {
    const updatePosition = (e: MouseEvent) => {
      positionRef.current = { x: e.clientX, y: e.clientY };
      isVisibleRef.current = true;
      hideTooltip.cancel();
      forceUpdate();
    };

    const handleMouseLeave = () => {
      hideTooltip();
    };

    window.addEventListener("mousemove", updatePosition, { passive: true });
    window.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", updatePosition);
      window.removeEventListener("mouseleave", handleMouseLeave);
      hideTooltip.cancel();
    };
  }, [hideTooltip, forceUpdate]);

  /**
   * Put it beside the pointer, and inside the window.
   *
   * Pinned to the cursor and lifted by its own height, a tooltip opened near
   * an edge is drawn off the screen - which is where half of one of these
   * went whenever a slice near the right of a chart was pointed at. Measured
   * after it is laid out and before it is painted, it can be moved to where
   * it actually fits: above the pointer when there is room above, below when
   * there is not, and never past either side.
   */
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const { width, height } = card.getBoundingClientRect();
    const { x, y } = positionRef.current;
    const gap = 12;
    const edge = 8;
    const left = Math.min(Math.max(edge, x), window.innerWidth - width - edge);
    const above = y - height - gap;
    const top =
      above >= edge
        ? above
        : Math.min(y + gap * 2, window.innerHeight - height - edge);
    card.style.left = `${Math.max(edge, left)}px`;
    card.style.top = `${Math.max(edge, top)}px`;
  });

  return (
    <FloatingPortal>
      <AnimatePresence>
        {isVisibleRef.current && (
          <motion.div
            ref={cardRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            style={{
              position: "fixed",
              left: positionRef.current.x,
              top: positionRef.current.y,
              pointerEvents: "none",
              zIndex: 9999,
            }}
          >
            <ChartTooltipContent
              values={values}
              datum={datum}
              keyColumnHeader={keyColumnHeader}
              valueColumnHeader={valueColumnHeader}
              size={size}
              colors={colors}
              renderKeyColumn={renderKeyColumn}
              renderValueColumn={renderValueColumn}
              checkIsActiveValue={checkIsActiveValue}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </FloatingPortal>
  );
}
